import { useEffect, useState } from 'react'
import { href, Link } from 'react-router'
import Button from '~/components/webshop-button'
import {
  acceptAllCookies,
  getConsentPreferences,
  rejectAllCookies,
  saveConsentPreferences,
  type CookieConsentPreferences
} from '~/utils/cookie-consent'

type ConsentCategory = {
  id: keyof Pick<CookieConsentPreferences, 'essential' | 'functional' | 'analytics'>
  label: string
  description: string
  required: boolean
}

const CONSENT_CATEGORIES: ConsentCategory[] = [
  {
    id: 'essential',
    label: 'Essentieel',
    description: 'Noodzakelijk voor de werking van de website (winkelwagen, authenticatie)',
    required: true
  },
  {
    id: 'functional',
    label: 'Functioneel',
    description: 'Onthoudt uw voorkeuren zoals ingevulde formuliergegevens',
    required: false
  },
  {
    id: 'analytics',
    label: 'Analytisch',
    description: 'Helpt ons de website te verbeteren door anoniem gebruik te meten',
    required: false
  }
]

export function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [preferences, setPreferences] = useState<CookieConsentPreferences>({
    essential: true,
    functional: false,
    analytics: false,
    consented: false,
    timestamp: 0
  })

  useEffect(() => {
    const current = getConsentPreferences()
    setPreferences(current)
    setShowBanner(!current.consented)
  }, [])

  const handleAcceptAll = () => {
    acceptAllCookies()
    setShowBanner(false)
  }

  const handleRejectAll = () => {
    rejectAllCookies()
    setShowBanner(false)
  }

  const handleSavePreferences = () => {
    saveConsentPreferences({
      functional: preferences.functional,
      analytics: preferences.analytics
    })
    setShowBanner(false)
  }

  const toggleCategory = (categoryId: keyof Pick<CookieConsentPreferences, 'functional' | 'analytics'>) => {
    setPreferences(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }))
  }

  if (!showBanner) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/50 p-4 sm:items-center">
      <div className="shadow-float w-full max-w-lg rounded-(--ws-radius) bg-white">
        <div className="p-6">
          <h2 className="mb-2 text-lg font-semibold text-gray-900">Cookievoorkeuren</h2>
          <p className="mb-4 text-sm text-gray-600">
            Wij gebruiken cookies om uw ervaring te verbeteren. U kunt hieronder uw voorkeuren aanpassen of al onze cookies accepteren.
          </p>

          {showDetails ? (
            <div className="mb-4 space-y-3">
              {CONSENT_CATEGORIES.map(category => (
                <label
                  key={category.id}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${
                    category.required ? 'cursor-default border-gray-200 bg-gray-50' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <input
                    type="checkbox"
                    checked={category.required || preferences[category.id]}
                    disabled={category.required}
                    onChange={() => !category.required && toggleCategory(category.id as 'functional' | 'analytics')}
                    className="mt-0.5 h-4 w-4 cursor-pointer rounded border-gray-300 accent-(--ws-btn2-bg) disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{category.label}</span>
                      {category.required && <span className="rounded bg-gray-200 px-1.5 py-0.5 text-xs text-gray-600">Vereist</span>}
                    </div>
                    <p className="mt-0.5 text-xs text-gray-500">{category.description}</p>
                  </div>
                </label>
              ))}

              <p className="text-xs text-gray-500">
                Lees ons{' '}
                <Link to={href('/privacy-policy')} className="text-(--ws-link) underline hover:no-underline">
                  privacybeleid
                </Link>{' '}
                voor meer informatie.
              </p>
            </div>
          ) : (
            <p className="mb-4 text-xs text-gray-500">
              Lees ons{' '}
              <Link to={href('/privacy-policy')} className="text-(--ws-link) underline hover:no-underline">
                privacybeleid
              </Link>{' '}
              voor meer informatie over hoe wij cookies gebruiken.
            </p>
          )}

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="order-3 cursor-pointer text-sm text-gray-500 underline hover:no-underline sm:order-1">
              {showDetails ? 'Minder opties' : 'Voorkeuren aanpassen'}
            </button>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button fullWidth={false} onClick={handleRejectAll}>
                Alles weigeren
              </Button>

              {showDetails ? (
                <Button
                  fullWidth={false}
                  onClick={handleSavePreferences}
                  bgColor="var(--ws-btn2-bg)"
                  bgColorHover="var(--ws-btn2-bg-hover)"
                  textColor="var(--ws-btn2-text)">
                  Voorkeuren opslaan
                </Button>
              ) : (
                <Button
                  fullWidth={false}
                  onClick={handleAcceptAll}
                  bgColor="var(--ws-btn2-bg)"
                  bgColorHover="var(--ws-btn2-bg-hover)"
                  textColor="var(--ws-btn2-text)">
                  Alles accepteren
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
